// Reading an audit log's `change` for people. The API sends it as a JSON string, as stored: for an update,
// the DeepDiff of the record before and after (values_changed, dictionary_item_added, …, keyed by paths
// such as root['primaryColour'] or, for a cell, root['Status/<row id>']['value']); for a few events, plain
// details such as {"error": "…"}. The raw string is left as it is for copying; this only describes it.

export interface ChangeEntry {
  /** What changed: "Primary colour", or a column's name. */
  label: string
  /** The value before; absent for something added. */
  from?: unknown
  /** The value after; absent for something removed. */
  to?: unknown
}

const ADDED = ['dictionary_item_added', 'iterable_item_added', 'attribute_added']
const REMOVED = ['dictionary_item_removed', 'iterable_item_removed', 'attribute_removed']
const CHANGED = ['values_changed', 'type_changes']
const DIFF_KEYS = [...ADDED, ...REMOVED, ...CHANGED]

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// Fields of a whole record that say nothing about what changed; they're still in the JSON.
const BOOKKEEPING = ['id', 'publicId', 'createdAt', 'updatedAt', 'deletedAt']
// Fields that name the record, shown first.
const NAMING = ['name', 'title', 'description']
const CAMEL_KEY = /^[a-z][a-zA-Z0-9]*$/

/** A key as words: primaryColour → "Primary colour". A column's own name is left as it is. */
const words = (key: string) => {
  if (!CAMEL_KEY.test(key)) return key
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * The name of what a DeepDiff path points at: its last part that isn't an id or an index. A cell's path
 * names it "<column>/<row id>", so that gives the column.
 */
export const labelFor = (path: string): string => {
  const parts = [...path.matchAll(/\[(?:'((?:[^'\\]|\\.)*)'|(\d+))\]/g)].map((match) => match[1] ?? match[2])
  const named = parts
    .map((part) => (part.includes('/') && UUID.test(part.split('/').pop() ?? '') ? part.split('/')[0] : part))
    .filter((part) => !UUID.test(part) && !/^\d+$/.test(part) && part !== 'value')
  return words(named[named.length - 1] ?? parts[parts.length - 1] ?? path)
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/** A change to a whole record as a change to each of its fields that differ, its name first. */
const fieldsOf = (kind: string, value: unknown): ChangeEntry[] => {
  const { old_value: oldValue, new_value: newValue } = (isRecord(value) ? value : {}) as Record<string, unknown>
  const before = CHANGED.includes(kind) ? oldValue : REMOVED.includes(kind) ? value : undefined
  const after = CHANGED.includes(kind) ? newValue : ADDED.includes(kind) ? value : undefined
  if (!isRecord(before) && !isRecord(after)) return [{ label: 'Value', from: before, to: after }]
  const from = isRecord(before) ? before : {}
  const to = isRecord(after) ? after : {}
  const keys = [...new Set([...Object.keys(from), ...Object.keys(to)])]
    .filter((key) => !BOOKKEEPING.includes(key) && JSON.stringify(from[key]) !== JSON.stringify(to[key]))
    .sort((a, b) => Number(NAMING.includes(b)) - Number(NAMING.includes(a)))
  return keys.map((key) => ({ label: words(key), from: from[key], to: to[key] }))
}

/** The change as a list of what changed, or null when there's nothing to show. */
export const parseChange = (change?: string | null): ChangeEntry[] | null => {
  if (!change || change === 'null' || change === '{}') return null
  let parsed: unknown
  try {
    parsed = JSON.parse(change)
  } catch {
    return [{ label: 'Change', to: change }]
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [{ label: 'Change', to: parsed }]

  const record = parsed as Record<string, unknown>
  const isDiff = Object.keys(record).some((key) => DIFF_KEYS.includes(key))
  if (!isDiff) return Object.entries(record).map(([key, value]) => ({ label: words(key), to: value }))

  const entries: ChangeEntry[] = []
  Object.entries(record).forEach(([kind, items]) => {
    // The whole record, as when something is created ({} before) or deleted ({} after): its fields instead.
    const whole =
      !Array.isArray(items) && items && typeof items === 'object' ? (items as Record<string, unknown>).root : undefined
    if (whole !== undefined) {
      entries.push(...fieldsOf(kind, whole))
      return
    }
    if (!items || typeof items !== 'object') return
    // An added or removed item can come as a list of paths (verbose_level 1) or a map of path to value.
    const pairs: Array<[string, unknown]> = Array.isArray(items)
      ? items.map((path: string) => [path, undefined])
      : Object.entries(items as Record<string, unknown>)
    pairs.forEach(([path, value]) => {
      const label = labelFor(path)
      if (CHANGED.includes(kind)) {
        const { old_value: from, new_value: to } = (value ?? {}) as { old_value?: unknown; new_value?: unknown }
        entries.push({ label, from, to })
      } else if (ADDED.includes(kind)) entries.push({ label, to: value })
      else if (REMOVED.includes(kind)) entries.push({ label, from: value })
    })
  })
  return entries.length > 0 ? entries : null
}

/** The raw change laid out for reading: indented JSON, or the text as it is when it isn't JSON. */
export const prettyJson = (change: string): string => {
  try {
    return JSON.stringify(JSON.parse(change), null, 2)
  } catch {
    return change
  }
}
